import {Button, ButtonBase, TextField, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "shared/StyledComponents";
import React, {ReactElement} from "react";
import {DW_SM, PD_MD, PD_SM, PD_XLG, PD_XLG2, PD_XXLG} from "shared/dimens";
import {BaseApp} from "shared/BaseApp";
import {Room, Rooms} from "./types";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "shared/BaseFragment";
import {getMemberAuth} from "shared/auth";
import {CustomDialogContent} from "shared/Dialogs";
import {PathProps} from "../index";
import {CreateOrJoinPage, HeaderButtons} from "./Components";
import {RoomLobbyFragment} from "./RoomLobbyFragment";
import {App} from "./App";
import {Action} from "../shared/types";

function ViewRoomCodeView(props: { room: Room, onDismiss: () => void }): ReactElement {
  const link = window.location.origin + "/r/" + props.room.id;
  return <CustomDialogContent
    style={{minWidth: DW_SM, width: null}}
    title="Your room code"
    customView={
      <StyledBoxColumn style={{gap: PD_XLG2}}>
        <StyledBoxColumn>
          <Typography
            style={{fontSize: "300%", fontWeight: "bold", textAlign: "center"}}>
            {props.room.id}
          </Typography>
          {props.room.joinedBy
            ? <Typography style={{textAlign: "center"}}>Ask anyone to enter this code to join.</Typography>
            : null}
        </StyledBoxColumn>
        <StyledBoxColumn>
          <Typography style={{textAlign: "center"}}>or go to this link</Typography>
          <StyledBoxRow>
            <TextField
              style={{flexGrow: 1}}
              size="small"
              disabled
              value={link}/>
            <Button
              onClick={() => navigator.clipboard.writeText(link).then(() => BaseApp.CONTEXT.showToast("Copied to clipboard."))}>Copy</Button>
          </StyledBoxRow>
        </StyledBoxColumn>
        <Button
          style={{
            paddingLeft: PD_XXLG,
            paddingRight: PD_XXLG
          }}
          variant="contained"
          onClick={() => props.onDismiss()}>
          Done
        </Button>
      </StyledBoxColumn>
    }/>;
}

export type RoomFragmentProps = BaseFragmentProps & {
  roomId: string,
}

type RoomFragmentState = BaseFragmentState & {
  room?: Room,
}

function ErrorView(props: { path: PathProps, text: string }): ReactElement {
  return <StyledBoxColumn style={{padding: PD_XLG, alignItems: "flex-start"}}>
    <Typography>{props.text}</Typography>
    <Button variant="contained" onClick={() => props.path.navigate("/")}>Go home</Button>
  </StyledBoxColumn>;
}

export class RoomFragment extends BaseFragment<RoomFragmentProps, RoomFragmentState> {

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    // await sleep(1000);
    this.setState({
      room: await Rooms.getInstance().getOrLoadItem(this.props.roomId),
    });
  }

  protected renderContainerContent(): React.ReactElement | null {
    let rendered: ReactElement;
    if (!this.state.room) {
      rendered = <ErrorView path={this.props.path} text={"Room does not exist."}/>;
    } else {
      const room = this.state.room;
      const memberId = getMemberAuth().getMemberId();
      const amICreator = memberId === room.creator;
      const amIJoiner = room.joinedBy && memberId === room.joinedBy;
      if (!amICreator && !amIJoiner) {
        if (room.joinedBy) {
          rendered = <ErrorView path={this.props.path} text={"Room is full."}/>;
        } else {
          rendered =
            <StyledBoxColumn style={{flexGrow: 1, alignItems: "center", justifyContent: "center", padding: PD_SM}}>
              <CreateOrJoinPage
                joinRoomId={room.id}
                path={this.props.path}
                text={room.creatorNickname + " invited you to join their buzz."}/>
            </StyledBoxColumn>
        }
      } else {
        rendered = <RoomLobbyFragment
          path={this.props.path}
          initialRoom={room}
          // onRoomDidReset={(room: Room) => this.setState({room: room})}
        />;
      }
    }
    const roomPath = "/r/" + (this.state.room?.id || "");
    const link = window.location.origin + roomPath;
    return <>
      <StyledBoxRow style={{
        width: "100%",
        zIndex: 1,
        height: 96,
        alignItems: "center",
        paddingLeft: PD_MD,
        paddingRight: PD_MD,
        boxSizing: "border-box",
      }}>
        <ButtonBase onClick={() => App.CONTEXT.showDialog(
          null,
          () => <ViewRoomCodeView room={this.state.room} onDismiss={() => App.CONTEXT.hideDialog()}/>)}>
          <img src={"/images/logotype.svg"} style={{height: 96, opacity: 1}}/>
        </ButtonBase>
        <StyledSpan/>
      </StyledBoxRow>
      <HeaderButtons actions={[new Action("Leave", () => this.props.path.navigate("/"))]}/>
      {rendered}
    </>;
  }
}